/**
 * Contamination Management Hook
 *
 * User Story: US-P2-008 - Contamination Management
 *
 * Provides hooks for:
 * - Reporting contamination events
 * - Fetching contamination history
 * - Getting contamination metrics
 * - Managing quarantine items
 */

import { useState, useEffect, useCallback } from 'react'
import { toLocalDateString } from '../utils/dateUtils'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY
const FUNCTION_URL = `${SUPABASE_URL}/functions/v1/contamination-events`

/**
 * Call the contamination-events edge function
 */
async function callContaminationFunction(action, data = {}) {
  const response = await fetch(FUNCTION_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      'apikey': SUPABASE_ANON_KEY
    },
    body: JSON.stringify({ action, ...data }),
  })

  const result = await response.json()

  if (!response.ok) {
    throw new Error(result.error || result.message || 'Request failed')
  }

  return result.data
}

/**
 * Hook for container contamination events
 */
export function useContainerContamination(containerId) {
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchEvents = useCallback(async () => {
    if (!containerId) {
      setEvents([])
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)
      const data = await callContaminationFunction('get_container_events', { container_id: containerId })
      setEvents(data || [])
    } catch (err) {
      console.error('Error fetching container contamination events:', err)
      setError(err.message)
      setEvents([])
    } finally {
      setLoading(false)
    }
  }, [containerId])

  useEffect(() => {
    fetchEvents()
  }, [fetchEvents])

  return { events, loading, error, refetch: fetchEvents }
}

/**
 * Hook for batch contamination events
 */
export function useBatchContamination(batchId) {
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchEvents = useCallback(async () => {
    if (!batchId) {
      setEvents([])
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)
      const data = await callContaminationFunction('get_batch_events', { production_batch_id: batchId })
      setEvents(data || [])
    } catch (err) {
      console.error('Error fetching batch contamination events:', err)
      setError(err.message)
      setEvents([])
    } finally {
      setLoading(false)
    }
  }, [batchId])

  useEffect(() => {
    fetchEvents()
  }, [fetchEvents])

  return { events, loading, error, refetch: fetchEvents }
}

/**
 * Hook for contamination rate metrics
 */
export function useContaminationRate(strainId = null, startDate = null, endDate = null) {
  const [metrics, setMetrics] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchMetrics = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await callContaminationFunction('get_rate', {
        strain_id: strainId,
        start_date: startDate,
        end_date: endDate,
      })
      setMetrics(data)
    } catch (err) {
      console.error('Error fetching contamination rate:', err)
      setError(err.message)
      setMetrics(null)
    } finally {
      setLoading(false)
    }
  }, [strainId, startDate, endDate])

  useEffect(() => {
    fetchMetrics()
  }, [fetchMetrics])

  return { metrics, loading, error, refetch: fetchMetrics }
}

/**
 * Hook for contamination breakdown by type
 */
export function useContaminationBreakdown(startDate = null, endDate = null) {
  const [breakdown, setBreakdown] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchBreakdown = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await callContaminationFunction('get_breakdown', {
        start_date: startDate,
        end_date: endDate,
      })
      setBreakdown(data || [])
    } catch (err) {
      console.error('Error fetching contamination breakdown:', err)
      setError(err.message)
      setBreakdown([])
    } finally {
      setLoading(false)
    }
  }, [startDate, endDate])

  useEffect(() => {
    fetchBreakdown()
  }, [fetchBreakdown])

  return { breakdown, loading, error, refetch: fetchBreakdown }
}

/**
 * Hook for overdue quarantine items
 */
export function useOverdueQuarantine() {
  const [overdueItems, setOverdueItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchOverdue = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await callContaminationFunction('get_overdue')
      setOverdueItems(data || [])
    } catch (err) {
      console.error('Error fetching overdue quarantine:', err)
      setError(err.message)
      setOverdueItems([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchOverdue()
  }, [fetchOverdue])

  return { overdueItems, loading, error, refetch: fetchOverdue }
}

/**
 * Hook for contamination mutations (report, update, resolve)
 */
export function useContaminationMutations() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const reportContamination = useCallback(async (eventData) => {
    try {
      setLoading(true)
      setError(null)
      const result = await callContaminationFunction('report', eventData)
      return result
    } catch (err) {
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  const updateContamination = useCallback(async (eventId, updates) => {
    try {
      setLoading(true)
      setError(null)
      const result = await callContaminationFunction('update', { event_id: eventId, ...updates })
      return result
    } catch (err) {
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  const resolveContamination = useCallback(async (eventId, resolution, notes = null) => {
    try {
      setLoading(true)
      setError(null)
      const result = await callContaminationFunction('resolve', {
        event_id: eventId,
        resolution,
        recovery_notes: notes,
        recovery_date: toLocalDateString(new Date()),
      })
      return result
    } catch (err) {
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  return {
    reportContamination,
    updateContamination,
    resolveContamination,
    loading,
    error,
  }
}
